/**
 * Barcodes.
 *
 * Every variant gets one automatically. We generate a 13-digit number that starts
 * with 2 (the range reserved worldwide for in-store codes, so it can never clash
 * with a manufacturer's barcode) and ends with a proper EAN-13 check digit.
 *
 * The same number prints as Code 128 (default, sharp on small tags) or as EAN-13
 * if the owner prefers it — one number, two symbologies, no migration.
 *
 * Numbers are handed out from a counter that only ever goes up, so a barcode is
 * never reused, even after a product is deleted.
 */

export type Symbology = 'CODE128' | 'EAN13';

export const INSTORE_PREFIX = '2';

/** Standard EAN-13 check digit over the first 12 digits. */
export function ean13CheckDigit(first12: string): number {
  if (!/^\d{12}$/.test(first12)) throw new Error('EAN-13 needs exactly 12 digits before the check digit');
  let sum = 0;
  for (let i = 0; i < 12; i++) {
    sum += Number(first12[i]) * (i % 2 === 0 ? 1 : 3);
  }
  return (10 - (sum % 10)) % 10;
}

export function isValidEan13(code: string): boolean {
  if (!/^\d{13}$/.test(code)) return false;
  return ean13CheckDigit(code.slice(0, 12)) === Number(code[12]);
}

/** Turn a sequence number into the store barcode for that variant. */
export function barcodeFromSequence(seq: number): string {
  if (!Number.isInteger(seq) || seq < 0 || seq > 99_999_999_999) {
    throw new Error('barcode sequence out of range');
  }
  const first12 = INSTORE_PREFIX + String(seq).padStart(11, '0');
  return first12 + ean13CheckDigit(first12);
}

/** Code 128 handles anything printable; EAN-13 must be 13 valid digits. */
export function isPrintable(code: string, symbology: Symbology): boolean {
  if (symbology === 'EAN13') return isValidEan13(code);
  return code.length > 0 && code.length <= 48 && /^[\x20-\x7E]+$/.test(code);
}

/** Cheap scanners send trailing whitespace or a stray Enter; clean it before lookup. */
export function normalizeScan(raw: string): string {
  return raw.replace(/[\r\n\t]/g, '').trim();
}

/** Receipt barcodes carry the receipt number, so a receipt can be scanned for an exchange. */
export function receiptBarcode(receiptNo: string): string {
  return receiptNo.toUpperCase();
}

/** Store-credit vouchers get their own scannable code. */
export function creditVoucherCode(seq: number): string {
  return `SC${String(seq).padStart(8, '0')}`;
}

export function isCreditVoucher(code: string): boolean {
  return /^SC\d{8}$/i.test(code.trim());
}

/** A gift card the till printed itself gets the same shape, under its own letters. */
export function giftCardCode(seq: number): string {
  return `GC${String(seq).padStart(8, '0')}`;
}

export function isGiftCardCode(code: string): boolean {
  return /^GC\d{8}$/i.test(code.trim());
}

/**
 * A code that may be written on a gift card.
 *
 * A shop can print its own vouchers, in which case the code is ours and looks
 * like GC00000001 - or it can buy plastic cards that already carry a barcode,
 * and then the code is whatever the supplier printed. So this is deliberately
 * permissive: letters, digits and dashes, long enough not to be typed by
 * accident and short enough to fit a card. What it must never do is accept a
 * RECEIPT number or a store-credit code, because activating a card under one of
 * those would make two different things answer to one code.
 */
export function isUsableCardCode(code: string): boolean {
  const c = code.trim().toUpperCase();
  if (!/^[A-Z0-9][A-Z0-9-]{5,23}$/.test(c)) return false;
  return !isReceiptNumber(c) && !isCreditVoucher(c);
}

export function isReceiptNumber(code: string): boolean {
  return /^[A-Z]{1,4}\d?-\d{4,8}$/i.test(code.trim());
}
